import { useUpdateProp } from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import ShadowStyle from 'client/components/core/Sidebar/components/Settings/styles/ShadowStyle'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { PaddingType } from 'common/types/styleTypes'
import React from 'react'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { headerColorsKeys } from 'src/editor/layout/sidebar/website-styles-settings/consts'
import { useCurrentTheme } from 'src/editor/layout/sidebar/website-styles-settings/hooks/useCurrentTheme'
import { ImageControl } from 'src/editor/shared/settings/controls/image-control'
import { PaddingControl } from 'src/editor/shared/settings/controls/padding-control'
import { ColorPickerGroup } from 'src/editor/shared/settings/groups/color-picker'
import { WebsiteHeaderInterface } from 'src/shared/types/website-entities'
import { getMobileOrDesktop } from 'src/shared/utils/get-mobile-or-desktop'
import { PaddingGroup } from '../../settings/groups/padding'
import { SizeAndPositionGroup } from '../../settings/groups/size-and-position'
import { EntityBorderGroup } from '../../settings/wrappers/border-groups/entity-border-group'

interface WebsiteHeaderDesignProps {
  entity: WebsiteHeaderInterface
}

export const WebsiteHeaderDesign = ({ entity }: WebsiteHeaderDesignProps) => {
  const { t } = useTypedTranslation()
  const updateProp = useUpdateProp(entity)
  const { currentTheme, onUpdate } = useCurrentTheme()
  const { isMobile } = useDeviceModeContext()

  return (
    <div className="pb-8">
      <GroupTitle className="mb-6" withoutHorizontalLine>
        {t('website_styles_settings.colors.label')}
      </GroupTitle>
      <ColorPickerGroup
        colorKeys={headerColorsKeys}
        elementKey="headerAndFooter"
        labelClassName="basis-auto"
        singleDeviceMode
      />
      <ImageControl
        label="website_theme_settings.group.header.background_picture"
        labelClassName="basis-auto"
        fileId={currentTheme.headerAndFooter.headerBackgroundPicture.file?.id}
        updateBackgroundFile={file =>
          onUpdate({
            headerAndFooter: {
              ...currentTheme?.headerAndFooter,
              headerBackgroundPicture: {
                ...currentTheme.headerAndFooter.headerBackgroundPicture,
                fileId: file?.id,
                file,
              },
            },
          })
        }
        update={background => {
          onUpdate({
            headerAndFooter: {
              ...currentTheme?.headerAndFooter,
              headerBackgroundPicture: {
                ...currentTheme.headerAndFooter.headerBackgroundPicture,
                background,
              },
            },
          })
        }}
        backgroundPosition={
          currentTheme.headerAndFooter.headerBackgroundPicture.background
        }
      />
      <EntityBorderGroup
        withoutHorizontalLine
        entity={entity}
        propertyKey="border"
        mobilePropertyKey="mobileBorder"
      />

      <SizeAndPositionGroup
        contentWidth={entity.contentWidth}
        padding={
          getMobileOrDesktop(
            entity.padding,
            entity?.mobilePadding,
            isMobile,
          ) as PaddingType
        }
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        updateContentWidth={updateProp('contentWidth')}
        sticky={entity?.sticky}
        htmlAttrId={entity.htmlAttrId}
        updateSticky={updateProp('sticky')}
        stickyOptions={{
          title: '',
          label: t('website_settings.sidebar.website_header.sticky'),
          tooltipText: t('settings_options.sticky.help_message'),
          isShowSelect: false,
          singleDeviceMode: true,
        }}
      />
      <ShadowStyle
        shadow={entity.boxShadow}
        mobileShadow={entity.mobileBoxShadow}
        update={updateProp('boxShadow')}
        mobileUpdate={updateProp('mobileBoxShadow')}
        withoutHorizontalLine
      />
    </div>
  )
}
